import * as React from 'react';
import isNil from 'lodash/isNil';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { __t, applyTranslations } from 'utils/language';
import { getAvailableSortOptions } from 'modules/proposal/utils/creator-proposal-utils';

import type {
  Data,
  MixedSelectDropdownItem,
  ProposalPageFilterOption,
  ProposalPageSortOption
} from 'types';
import type {
  ProposalTileChannelTypeOptions
} from 'modules/proposal/types/ProposalTile.type';

import './proposal-tile-filter.scss';

applyTranslations(require('./proposal-tile-filter.i18n.json'));

const FILTER_LIST_APPROVE: Array<ProposalPageFilterOption> = [
  'all',
  'approved',
  'pending',
  'declined'
];

const FILTER_LIST_CREATE: Array<ProposalPageFilterOption> = [
  ...FILTER_LIST_APPROVE,
  'incomplete'
];

type DropdownKey = ProposalPageFilterOption | ProposalPageSortOption;

type Props = {
  filter: ProposalPageFilterOption,
  sort: ProposalPageSortOption,
  isBrandPage?: boolean,
  currentChannels: Array<ProposalTileChannelTypeOptions>,
  onFilterChange: (newOption: ProposalPageFilterOption) => void,
  onSortChange: (newOption: ProposalPageSortOption) => void
};

const ProposalTileFilter = ({
  filter = 'all',
  sort = 'ig_desc',
  isBrandPage = false,
  currentChannels,
  onFilterChange,
  onSortChange
}: Props) => {
  const getDropdownItems = (
    keys: Array<DropdownKey>,
    type: string
  ): Array<MixedSelectDropdownItem> => (
    keys.map(key => ({
      identifier: key,
      isDisabled: false,
      isSelected: type === 'filter' ? filter === key : sort === key,
      label: __t(`proposal_tile_filter.${type}_options.values.${key}`),
      value: key
    }))
  );

  const handleDropdownChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const value = selectedItem?.identifier || updatedItems[0].identifier;
    const type = data?.type;

    if (isNil(type)) {
      return;
    }

    if (type === 'filter') {
      onFilterChange(value as ProposalPageFilterOption);
      return;
    }

    onSortChange(value as ProposalPageSortOption);
  };

  const filterList = getDropdownItems((isBrandPage) ? FILTER_LIST_APPROVE : FILTER_LIST_CREATE, 'filter');
  const sortList = getDropdownItems(getAvailableSortOptions(currentChannels), 'sort');

  return (
    <div className="proposal-tile-filters">
      <div className="proposal-tile-filter">
        <div className="proposal-tile-filter__label">
          {__t('proposal_tile_filter.filter_options.label')}
        </div>
        <MixedSelectDropdown
          key={`proposal-tile-filter__dropdown--${filter}`}
          name="proposal-tile-filter__dropdown--filter"
          items={filterList}
          withSelectedInList
          className="proposal-tile-filter__dropdown"
          data={{ type: 'filter' }}
          onItemsToggle={handleDropdownChange}
        />
      </div>
      <div className="proposal-tile-filter">
        <div className="proposal-tile-filter__label">
          {__t('proposal_tile_filter.sort_options.label')}
        </div>
        <MixedSelectDropdown
          key={`proposal-tile-filter__dropdown--${sort}`}
          name="proposal-tile-filter__dropdown--sort"
          items={sortList}
          withSelectedInList
          className="proposal-tile-filter__dropdown"
          data={{ type: 'sort' }}
          onItemsToggle={handleDropdownChange}
        />
      </div>
    </div>
  );
};

export default ProposalTileFilter;
